import { EMPTY_COMMENTS_DATA } from '@/constants/comment'
import { getCmsBaseURL } from '@/lib/configs'
import { Locale } from '@/server/constants/locales'
import { setPasscodeCookie } from '@/server/lib/passcode'
import {
  API_ADD_MY_ROOMS_V2,
  API_ADD_ROOM_UPLOAD_ASSET,
  API_DELETE_CO_OWNED_ROOM,
  API_DELETE_MY_ROOMS_V2,
  API_ENDPOINT_CO_OWNERS,
  API_ENDPOINT_COMMENTS_COUNT,
  API_ENDPOINT_COMMENTS_V2,
  API_GET_ACTIVITY_SHOWCASE,
  API_GET_CREATOR_TUTORIAL,
  API_GET_EDITOR_CHOICES,
  API_GET_EVENTS,
  API_GET_FOLLOWING_ROOMS,
  API_GET_GUEST_INFO,
  API_GET_HUBS,
  API_GET_LIKED_ROOMS_V3,
  API_GET_ME_V4,
  API_GET_MY_GIVEAWAY_LIST,
  API_GET_MY_ROOMS_V2,
  API_GET_NFT,
  API_GET_RECOMMEND_CREATORS,
  API_GET_RECOMMEND_WORLDS,
  API_GET_ROOM_PREFERRED_DEVICES,
  API_GET_ROOM_SETTINGS,
  API_GET_ROOM_TAGS,
  API_GET_ROOMS_V1,
  API_GET_ROOMS_V2,
  API_GET_ROOMS_V3,
  API_GET_SCENES,
  API_GET_SEARCH_ACCOUNTS,
  API_GET_STANDALONE_CONTENT,
  API_GET_THEMES,
  API_GET_USER_ROOMS_V2,
  API_GET_VOUCHER_ROOMS_V2,
  API_UPDATE_ACCOUNTS_V3,
  API_UPDATE_ACCOUNTS_V4,
  API_UPDATE_MY_ROOMS,
} from '@/services/api'
import { CommentsData } from '@/types/comments'
import { DeviceTypeWithAndroidIOS, EntryPass, VendorType } from '@/types/configs'
import { BannerMeta, Media, SocialLink } from '@/types/store'
import { ImageAsset, PolicyValue, VideoAsset } from '@/types/world'
import { env } from 'next-runtime-env'

// Basic types
export type PaginationPages = {
  total: number
  start: number
  limit: number
  prev: string
  next: string
}

export type Host = {
  id?: string
  display_name?: string
}

export type Speaker = {
  id?: string
  display_name?: string
  description?: string
  image?: string
}

export type Owner = {
  id: string
  display_name: string
  snapshot_url: string
  htc_account_id: string
  head_icon_url: string
  community_user_id?: string
}

type BaseRoom = {
  id: string
  title: string
  description: string
  description_plaintext: string
  image: string
  hub_sid: string
  hubs_url: string
  scene_sid: string
  policy: PolicyValue
  view_count: number
  like_count: number
  is_protected: boolean
  is_opened: boolean
  is_voucher_room: boolean
  is_live?: boolean
  is_liked?: boolean
  owner: Owner
  logo_overlay_position?: LogoPosition
  github_url?: string
}

export type Room = BaseRoom & {
  description?: string
  is_distributed_room: boolean
  total_vram_kb: number
  online_counts?: number
  tags?: string[]
  external_link: string
  preferred_devices: DeviceTypeWithAndroidIOS[]
  is_standalone: boolean
  is_adult_only: boolean
  is_featured?: boolean
  is_voucher_room: boolean
}

type CreatorTutorial = {
  title: string
  description: string
  image: string
  action_link: string
}

export type UserWorld = BaseRoom & {
  max_presence_capacity: number
  max_instance_capacity: number
  theme: string
  features: {
    spatial_sound: boolean
  }
}

export type RecommendWorlds = {
  id: string
  image: string
  banner_image: string
  title: string
  description: string
  is_pinned: boolean
  rooms: Room[]
}

export type ThemeRoom = BaseRoom & {
  theme_order: number
  is_distributed_room: boolean
  online_counts?: number
}

export type SubImage = {
  directus_files_id?: string
}

export type Video = {
  video_id?: string
}

export type HashTag = {
  id?: string
  value: string
}

export type Category = {
  id?: string
  value: string
}

export type EditorChoice = {
  id: string
  image: string
  banner_image: string
  title: string
  description: string
  is_pinned: boolean
  rooms: Room[]
  is_device_filter_enabled?: boolean
  preferred_devices?: DeviceTypeWithAndroidIOS[]
}

// function types
export type Event = {
  id: string
  image: string
  title: string
  description: string
  logo?: string
  agenda?: string
  start_time?: string
  end_time?: string
  is_liked?: boolean
  is_promoted?: boolean
  like_count?: number
  view_count?: number
  hosts: Host[]
  speakers: Speaker[]
  rooms: Room[]
  sub_images: SubImage[]
  videos: Video[]
  hashtags: HashTag[]
  category: Category[]
}

export type EventsResults = {
  results: Event[]
  pages: PaginationPages
}

export type ActivityShowcaseList = {
  background?: string
  video?: VideoAsset
  description: string
  description_html?: string
  title: string
  title_html?: string
  action_title: string
  action_link: string
  card?: string
  view_count?: number
  like_count?: number
  online_counts?: number
  preferred_devices?: DeviceTypeWithAndroidIOS[]
  enable_no_signup_hint?: boolean
}

export type TrailerMedia = {
  trailer_videos?: Array<{
    vod_id: string
    file_id: string
    cover_image?: string
  }>
  trailer_images?: Array<{
    id?: string
    url?: string
  }>
}

export enum ActivityShowcaseCategory {
  Landing = 'landing',
  Explore = 'explore',
}

export const fetchCMSApiWithStatusCode = <Response = Record<string, unknown>>(
  url: string,
  method: RequestInit['method'] = 'GET',
  payload: RequestInit['body'],
  params: Omit<RequestInit, 'method' | 'body'> = {},
  htcToken = '',
  isSSR: boolean = false,
): Promise<{ status: number; results: Response; headers: Headers } | void> => {
  const cmsBaseURL = getCmsBaseURL(isSSR)
  const cmsUrl = `${cmsBaseURL}${url}`

  const init: RequestInit = {
    headers: {
      'content-type': 'application/json',
    },
    method,
    ...params,
  }
  if (htcToken) {
    init.headers = {
      ...init.headers,
      ...{ authkey: htcToken },
    }
  }
  if (isSSR) {
    const userAgent = env('USER_AGENT')
    if (userAgent) {
      init.headers = {
        ...init.headers,
        ...{ 'User-Agent': userAgent },
      }
    }
  }
  if (payload) {
    init.body = JSON.stringify(payload)
  }
  return fetch(cmsUrl, init)
    .then(async (response) => {
      const result = await response.text()
      try {
        return { status: response.status, results: JSON.parse(result), headers: response.headers }
      } catch (e) {
        // Don't return json.
        return { status: response.status, results: result, headers: response.headers }
      }
    })
    .catch((error) => {
      console.error('fetchCMSApiWithStatusCode error:', error)
    })
}

export function getEditorChoices(
  start: number = 0,
  limit: number = 0,
  category: 'landing' | 'explore' = 'landing',
  locale?: string,
  totalVramKb?: number,
  isSSR: boolean = true,
) {
  const queryString = new URLSearchParams({
    start: start.toString(),
    limit: limit.toString(),
    category,
    ...(typeof totalVramKb === 'number' && { total_vram_kb: totalVramKb.toString() }),
    ...(locale && { locale }),
  }).toString()

  return fetchCMSApiWithStatusCode<PaginatedResponse<EditorChoice>>(
    `${API_GET_EDITOR_CHOICES}?${queryString}`,
    'GET',
    null,
    {},
    '',
    isSSR,
  )
}

export function getActivityShowcase(
  category: ActivityShowcaseCategory = ActivityShowcaseCategory.Landing,
  { start = 0, limit = 0, locale = '' },
  isSSR: boolean = true,
) {
  return fetchCMSApiWithStatusCode<PaginatedResponse<ActivityShowcaseList>>(
    `${API_GET_ACTIVITY_SHOWCASE}?start=${start}${
      locale ? `&locale=${locale}` : ''
    }&limit=${limit}&category=${category}`,
    'GET',
    null,
    {},
    '',
    isSSR,
  )
}

export function getEvents(params: Partial<EventsQueryParams> = {}, isSSR: boolean = false) {
  const url = getAppendedQueryUrl(API_GET_EVENTS, params, isSSR)
  return fetchCMSApiWithStatusCode<PaginatedResponse<Event>>(url, 'GET', null, {}, '', isSSR)
}

export function getTheme(themeId: string) {
  return fetchCMSApiWithStatusCode<Theme>(`${API_GET_THEMES}/${themeId}`, 'GET', null, {}, '', true)
}

export function postLikeRoom(roomId: string, htcToken: string) {
  return fetchCMSApiWithStatusCode<Room>(
    `${API_GET_ROOMS_V1}/${roomId}/liked`,
    'POST',
    null,
    {},
    htcToken,
  )
}

export function postUnlikeRoom(roomId: string, htcToken: string) {
  return fetchCMSApiWithStatusCode<Room>(
    `${API_GET_ROOMS_V1}/${roomId}/unliked`,
    'POST',
    null,
    {},
    htcToken,
  )
}

export function getRoom(roomId: string, htcToken: string) {
  return fetchCMSApiWithStatusCode<Room>(`${API_GET_ROOMS_V1}/${roomId}`, 'GET', null, {}, htcToken)
}

export type RoomsResults = {
  results: Room[]
  pages: PaginationPages
}

export function getMyRooms(htcToken: string, start: number = 0, limit: number = 0) {
  return fetchCMSApiWithStatusCode<PaginatedResponse<Room>>(
    `${API_GET_MY_ROOMS_V2}?limit=${limit}&start=${start}`,
    'GET',
    null,
    {},
    htcToken,
  )
}

export function getCoOwnedRooms(htcToken: string) {
  return fetchCMSApiWithStatusCode<PaginatedResponse<Room>>(
    `${API_GET_ME_V4}/co-owned-worlds`,
    'GET',
    null,
    {},
    htcToken,
  )
}

type RoomSettings = {
  devices: 'others' | 'iphone' | 'android' | 'mobile_vr'[]
  max_presence_size: number
  max_room_instance: number
  scene: {
    id: string
    scene_sid: string
    version: string
    scene_type: string
    owner: string
    scene_meta: {
      scene_url: string
      config_url: string
      game_scripts_url: string
      preview_url: string
    }
    settings: {
      disabledViverseSystem: boolean
      automaticOcclusionCulling: boolean
    }
  }
}

export function getRoomSettings(hubSid: string) {
  return fetchCMSApiWithStatusCode<RoomSettings[]>(
    `${API_GET_ROOM_SETTINGS}?hub_sid=${hubSid}`,
    'GET',
    null,
    {},
  )
}

type QueryParams = {
  start: number
  limit: number
  keyword: string
}

type RoomsQueryParams = QueryParams & {
  locale: Locale
  filter: string
  account_id: string
  htc_account_id: string
  nft_chain: string
  hub_sid: string
  tag: string
  total_vram_kb: number
  is_voucher_room?: boolean
}

type EventsQueryParams = QueryParams & {
  locale: Locale
  filter: string
  total_vram_kb: number
  status: 'opened' | 'closed' | 'soon'
}

type SearchAccountsQueryParams = QueryParams & {}

function getAppendedQueryUrl(url: string, params: any, isSSR: boolean = false) {
  const cmsBaseURL = getCmsBaseURL(isSSR)
  const fullUrl = `${cmsBaseURL}${url}`
  const _url = new URL(fullUrl)

  Object.entries(params).forEach(([key, value]) => {
    const valueString = value + ''
    if (valueString) {
      _url.searchParams.append(key, valueString)
    }
  })

  return `${_url.pathname}${_url.search}`
}

export function getPublicRooms(
  params: Partial<RoomsQueryParams>,
  htcToken?: string,
  isSSR = false,
) {
  const url = getAppendedQueryUrl(API_GET_ROOMS_V2, params, isSSR)
  return fetchCMSApiWithStatusCode<PaginatedResponse<Room>>(url, 'GET', null, {}, htcToken, isSSR)
}

export function getLikedRooms(
  htcToken: string,
  params: Partial<RoomsQueryParams>,
  isSSR: boolean = false,
) {
  const url = getAppendedQueryUrl(API_GET_LIKED_ROOMS_V3, params, isSSR)
  return fetchCMSApiWithStatusCode<PaginatedResponse<Room>>(url, 'GET', null, {}, htcToken, isSSR)
}

export type UserProfile = {
  id: string
  wallet_address: string
  htc_account_email: string
  htc_account_phone_number: string
  display_name: string
  htc_account_id: string
  like_rooms: Room[]
  liked_events: Event[]
  oursong_account_id: string
  community_user_id: string
  is_over_18: boolean
  active_avatar: {
    snapshot_url: string
    head_icon_url: string
    gender: number
    is_half_body: string
    data_type: string
  }
  bio?: string
  profile_banner: string
  profile_banner_meta?: BannerMeta
  profile_media: Media[]
  show_social_links: boolean
  social_links?: SocialLink[]
}

export function getUserProfile(htcAccountId: string) {
  return fetchCMSApiWithStatusCode<Partial<UserProfile>>(
    `${API_GET_GUEST_INFO}?htc_account_id=${htcAccountId}`,
    'GET',
    null,
    {},
    '',
    true,
  )
}

export type recommendCreatorResults = {
  results: Creator[]
  pages: PaginationPages
}

export function getRecommendCreators(htcToken: string) {
  return fetchCMSApiWithStatusCode<PaginatedResponse<Creator>>(
    API_GET_RECOMMEND_CREATORS,
    'GET',
    null,
    {},
    htcToken,
  )
}

export type Creator = {
  id: string
  community_user_id: string
  display_name: string
  head_icon_url: string
  htc_account_id: string
}

export type Selection = {
  title: string
  scene_sid: string
  images: ImageAsset[]
  videos: VideoAsset[]
  nftData?: {
    nftVendor: NFTVendor
    externalData: any
  }
}

export type SelectedItem = {
  item: string
  redeem_time: string
}

export type Giveaway = {
  id: string
  title: string
  room_size: number
  chance: number
  type: 'scene'
  selections: Selection[]
  selected: SelectedItem[]
}

export type DigitalAssetPod = {
  chain: string
  contract: string
  contract_type: string
  token_id: string
  vendor_type: string
  token_source: {
    name: string
    description: string
    animation_url: string
    image: string
    scene_sid: string
    store_link: string
  }
  external_data: {
    product_id: string
    product_item_id: string
    product_asset_id: string
    urlKey: string
  }
}

export type DigitalAssetPodResults = {
  result: DigitalAssetPod[]
  pages: PaginationPages
}

export function getMyGiveaways(htcToken: string) {
  return fetchCMSApiWithStatusCode<PaginatedResponse<Giveaway>>(
    API_GET_MY_GIVEAWAY_LIST,
    'GET',
    null,
    {},
    htcToken,
  )
}

export function getAScene(htcToken: string, sceneSid: string) {
  return fetchCMSApiWithStatusCode<PaginatedResponse<Giveaway>>(
    `${API_GET_SCENES}/${sceneSid}`,
    'GET',
    null,
    {},
    htcToken,
  )
}

export type NFTVendor = 'generic_nft' | 'vivebytes' | 'sketchfab_id' | 'oursong'
export type LogoPosition =
  | 'top-left'
  | 'top-center'
  | 'top-right'
  | 'bottom-left'
  | 'bottom-center'
  | 'bottom-right'

export type CreateRoomData = {
  scene_sid: string
  policy: Room['policy']
  title: string
  image: ImageAsset['id']
  features: {
    spatial_sound: boolean
  }
  passcode?: string
  description_plaintext: string
  tags?: string
  max_presence_size?: number
  nft_vendor?: NFTVendor
  external_data?: any
  preferred_devices?: string[]
  is_adult_only?: boolean
  logo_overlay_position?: LogoPosition
  github_url?: string
}

export function postCreateAGiveawayRoom(
  htcToken: string,
  giveawayId: Giveaway['id'],
  data: CreateRoomData,
) {
  return fetchCMSApiWithStatusCode<Room>(
    `${API_GET_MY_GIVEAWAY_LIST}/${giveawayId}/create-room`,
    'POST',
    // @ts-ignore
    data,
    {},
    htcToken,
  )
}

export function postCreateMyRoom(htcToken: string, data: CreateRoomData) {
  return fetchCMSApiWithStatusCode<Room>(
    API_ADD_MY_ROOMS_V2,
    'POST',
    // @ts-ignore
    data,
    {},
    htcToken,
  )
}

export function getDigitalAssetPods(
  htcToken: string,
  profileId: string,
  vendorType: string,
  chainName: string,
) {
  return fetchCMSApiWithStatusCode<DigitalAssetPodResults>(
    `${API_GET_NFT}/${profileId}/nft/${chainName}/${vendorType}/pods?curating_only=true`,
    'GET',
    null,
    {},
    htcToken,
  )
}

type UploadRoomImageResponse = {
  id: string
  url: string
}

export const fetchUploadRoomImage = async (htcToken: string, file: File) => {
  const formData = new FormData()
  formData.append('asset', file)
  const params = {
    headers: {},
    body: formData,
  }

  return fetchCMSApiWithStatusCode<UploadRoomImageResponse>(
    API_ADD_ROOM_UPLOAD_ASSET,
    'POST',
    null,
    params,
    htcToken,
  )
}

export function deleteRoom(htcToken: string, roomId: string) {
  return fetchCMSApiWithStatusCode<RoomsResults>(
    `${API_DELETE_MY_ROOMS_V2}/${roomId}`,
    'DELETE',
    null,
    {},
    htcToken,
  )
}

export function withdrawCoOwnedRoom(htcToken: string, roomId: string, profileId: string) {
  return fetchCMSApiWithStatusCode<RoomsResults>(
    `${API_DELETE_CO_OWNED_ROOM}/${roomId}/co-owners/${profileId}`,
    'DELETE',
    null,
    {},
    htcToken,
  )
}

export type EntryPassResults = {
  result: EntryPass[]
  pages: PaginationPages
}

export function getEntryPass(
  htcToken: string,
  profileId: string,
  chain: string = 'eth',
  vendorType: VendorType = 'vivebytes',
) {
  return fetchCMSApiWithStatusCode<EntryPassResults>(
    `${API_GET_NFT}/${profileId}/nft/${chain}/${vendorType}/vouchers`,
    'GET',
    null,
    {},
    htcToken,
  )
}

export function getUserEntryPassByCreator(
  htcToken: string,
  profileId: string,
  creatorId: string,
  chain: string = 'digital_asset',
  vendorType: VendorType = 'vivebytes',
) {
  return fetchCMSApiWithStatusCode<EntryPassResults>(
    `${API_GET_NFT}/${profileId}/nft/${chain}/${vendorType}/vouchers?creator_account_id=${creatorId}`,
    'GET',
    null,
    {},
    htcToken,
  )
}

export type PatchRoomData = {
  policy: Room['policy']
  title: string
  image: ImageAsset['id']
  passcode?: string
  features: {
    spatial_sound: boolean
  }
  description_plaintext: string
  max_presence_size?: number
}

export function patchRoom(htcToken: string, roomId: string, data: PatchRoomData) {
  return fetchCMSApiWithStatusCode<Room>(
    `${API_UPDATE_MY_ROOMS}/${roomId}`,
    'PATCH',
    // @ts-ignore
    data,
    {},
    htcToken,
  )
}

export type Tag = {
  name: string
}
export type PaginatedResponse<T> = { results: T[]; pages: PaginationPages }

export function getRoomTags(isSSR = true) {
  return fetchCMSApiWithStatusCode<PaginatedResponse<Tag>>(
    API_GET_ROOM_TAGS,
    'GET',
    null,
    {},
    '',
    isSSR,
  )
}

export function getRoomPreferredDevices(isSSR = true) {
  return fetchCMSApiWithStatusCode<PaginatedResponse<Tag>>(
    API_GET_ROOM_PREFERRED_DEVICES,
    'GET',
    null,
    {},
    '',
    isSSR,
  )
}

export type Theme = {
  id: string
  title: string
  description: string
  image: string
  banner_image: string
  policy: string
  hosted: string
  logo: string
  rooms: ThemeRoom[]
}

export function getThemes() {
  return fetchCMSApiWithStatusCode<PaginatedResponse<Theme>>(
    API_GET_THEMES,
    'GET',
    null,
    {},
    '',
    true,
  )
}

export function getFollowingRooms(
  htcToken: string,
  start: number = 0,
  limit: number = 0,
  totalVramKb: number,
) {
  return fetchCMSApiWithStatusCode<PaginatedResponse<Room>>(
    `${API_GET_FOLLOWING_ROOMS}?limit=${limit}&start=${start}&total_vram_kb=${totalVramKb}`,
    'GET',
    null,
    {},
    htcToken,
  )
}

export function getGuestAccount(htcToken: string, htcAccountId: string) {
  return fetchCMSApiWithStatusCode<UserProfile>(
    `${API_GET_GUEST_INFO}?htc_account_id=${htcAccountId}`,
    'GET',
    null,
    {},
    htcToken,
  )
}

export type SearchAccount = {
  id: string
  community_user_id: string
  display_name: string
  head_icon_url: string
  htc_account_id: string
}

export function getSearchAcounts(
  params: Partial<SearchAccountsQueryParams> = {},
  isSSR: boolean = false,
) {
  const url = getAppendedQueryUrl(API_GET_SEARCH_ACCOUNTS, params, isSSR)
  return fetchCMSApiWithStatusCode<PaginatedResponse<SearchAccount>>(
    url,
    'GET',
    null,
    {},
    '',
    isSSR,
  )
}

export function getUserWorlds(
  htcToken: string | undefined,
  {
    start = 0,
    limit = 0,
    vram,
    sort,
    filter,
    keyword,
    locale,
    device,
  }: {
    start: number
    limit: number
    vram?: number
    sort?: string
    filter?: string
    keyword?: string
    locale?: string
    device: DeviceTypeWithAndroidIOS
  },
) {
  const queryString = new URLSearchParams({
    limit: limit.toString(),
    start: start.toString(),
    ...(sort && { sort }),
    ...(filter && { filter }),
    ...(keyword && { keyword }),
    ...(vram && { total_vram_kb: vram.toString() }),
    ...(locale && { locale }),
    ...(device && { device: device === 'mobile' ? 'ios,android' : device }),
  }).toString()

  return fetchCMSApiWithStatusCode<PaginatedResponse<UserWorld>>(
    `${API_GET_USER_ROOMS_V2}?${queryString}`,
    'GET',
    null,
    {},
    htcToken,
  )
}

export function getRecommendWorlds(
  htcToken: string | undefined,
  {
    limit = 0,
    locale,
    device,
    random = false,
  }: {
    limit?: number
    locale?: string
    device: DeviceTypeWithAndroidIOS
    random?: boolean
  },
  isSSR = false,
) {
  const queryString = new URLSearchParams({
    limit: limit.toString(),
    ...(locale && { locale }),
    ...(device && { device: `recommend_${device}` }),
    random: random ? 'true' : 'false',
  }).toString()

  return fetchCMSApiWithStatusCode<RecommendWorlds>(
    `${API_GET_RECOMMEND_WORLDS}?${queryString}`,
    'GET',
    null,
    {},
    htcToken,
    isSSR,
  )
}

export function getRandomRecommendWorlds(
  htcToken: string | undefined,
  {
    device,
    limit = 0,
  }: {
    device: DeviceTypeWithAndroidIOS
    limit?: number
  },
  isSSR = false,
) {
  return getRecommendWorlds(htcToken, { device, limit, random: true }, isSSR).then(
    (res) => res?.results.rooms,
  )
}

export function postFeaturedRoom(htcToken: string, roomId: string) {
  return fetchCMSApiWithStatusCode<Room>(
    `${API_GET_MY_ROOMS_V2}/${roomId}/featured`,
    'POST',
    null,
    {},
    htcToken,
  )
}

export function deleteFeaturedRoom(htcToken: string, roomId: string) {
  return fetchCMSApiWithStatusCode<Room>(
    `${API_GET_MY_ROOMS_V2}/${roomId}/featured`,
    'DELETE',
    null,
    {},
    htcToken,
  )
}

export function getFeaturedRooms(
  params: Partial<RoomsQueryParams>,
  htcToken?: string,
  cmsAccountId?: string,
  isSSR = false,
) {
  const url = getAppendedQueryUrl(
    `${API_UPDATE_ACCOUNTS_V4}/${cmsAccountId}/featured-rooms`,
    params,
    isSSR,
  )

  return fetchCMSApiWithStatusCode<PaginatedResponse<Room>>(url, 'GET', null, {}, htcToken, isSSR)
}

export function getCreatorTutorial(locale: string, isSSR = true) {
  const queryString = new URLSearchParams({
    ...(locale && { locale }),
  }).toString()

  return fetchCMSApiWithStatusCode<PaginatedResponse<CreatorTutorial>>(
    `${API_GET_CREATOR_TUTORIAL}?${queryString}`,
    'GET',
    null,
    {},
    '',
    isSSR,
  )
}

export type StandaloneContentResult = {
  publish_url?: string
}

export function getStandaloneContent(htcToken: string, sceneSid: string) {
  return fetchCMSApiWithStatusCode<StandaloneContentResult>(
    `${API_GET_STANDALONE_CONTENT}/${sceneSid}`,
    'GET',
    null,
    {},
    htcToken,
  )
}

export function getSpacesByRoomId(
  roomId: string,
  params: Omit<RequestInit, 'method' | 'body'> = {},
  htcToken?: string,
  isSSR = true,
) {
  return fetchCMSApiWithStatusCode<PaginatedResponse<Room>>(
    `${API_GET_ROOMS_V2}?hub_sid=${roomId}`,
    'GET',
    null,
    params,
    htcToken,
    isSSR,
  )
}

export const getInitialRoomData = async (roomId: string, htcToken?: string, isSSR?: boolean) => {
  // 🥷🏼 caching here for layout, page use together
  const res = await getSpacesByRoomId(roomId, {}, htcToken, isSSR)
  return res?.results?.results?.[0]
}

export type CoOwner = {
  id: string
  display_name: string
  head_icon_url: string
}

export const getRoomCoOwners = async (roomId: string, htcToken?: string, isSSR = true) => {
  return fetchCMSApiWithStatusCode<PaginatedResponse<CoOwner>>(
    `${API_GET_ROOMS_V3}/${roomId}/co-owners`,
    'GET',
    null,
    {},
    htcToken,
    isSSR,
  )
}

export const getCoOwnersByRoomId = async (roomData: Room | undefined) => {
  const response = await getRoomCoOwners(roomData!.id)
  return response?.results?.results
}

export function getCoOwners(htcToken: string | undefined, roomId: string) {
  return fetchCMSApiWithStatusCode<PaginatedResponse<Owner>>(
    `${API_ENDPOINT_CO_OWNERS}/${roomId}/co-owners`,
    'GET',
    null,
    {},
    htcToken,
  )
}

export function updateRoomCoOwners(htcToken: string | undefined, roomId: string, coOwnersList: []) {
  return fetchCMSApiWithStatusCode<PaginatedResponse<Owner>>(
    `${API_ENDPOINT_CO_OWNERS}/${roomId}/co-owners`,
    'PATCH',
    // @ts-expect-error
    { co_owners: coOwnersList },
    {},
    htcToken,
  )
}

export const fetchTrailerMedia = (roomId: string, htcToken?: string) => {
  return fetchCMSApiWithStatusCode<TrailerMedia>(
    `${API_GET_USER_ROOMS_V2}/${roomId}/trailer-media`,
    'GET',
    null,
    {},
    htcToken,
  )
}

export const updateTrailerMedia = (
  htcToken: string,
  roomId: string,
  trailerMedia: TrailerMedia,
) => {
  return fetchCMSApiWithStatusCode<TrailerMedia>(
    `${API_GET_USER_ROOMS_V2}/${roomId}/trailer-media`,
    'PATCH',
    // @ts-ignore
    trailerMedia,
    {},
    htcToken,
  )
}

export const addWorldViewCount = (worldId: string) => {
  return fetchCMSApiWithStatusCode<PaginatedResponse<Room>>(
    `${API_GET_HUBS}/${worldId}/viewed`,
    'POST',
    null,
    {},
  )
}

export const updateUserAge = (profileId: string, isOver18: boolean, htcToken: string) => {
  return fetchCMSApiWithStatusCode<UserProfile>(
    `${API_UPDATE_ACCOUNTS_V3}/${profileId}`,
    'PATCH',
    // @ts-ignore
    { is_over_18: isOver18 },
    {},
    htcToken,
  )
}

export async function validatePasscode(
  passcode: string,
  roomId: string,
  htcAuthKey = '',
): Promise<{ isPass: boolean; suffix?: string }> {
  const response = await fetchCMSApiWithStatusCode(
    `/api/hubs-cms/v1/passcode/${roomId}`,
    'POST',
    // @ts-ignore
    { passcode },
    {},
    htcAuthKey,
    true,
  )

  if (response?.status !== 200) return { isPass: false }

  const cookieSuffix = await setPasscodeCookie(roomId, passcode)
  return { isPass: true, suffix: cookieSuffix }
}

export const getComments = async ({
  roomId,
  parentId,
  limit = 50,
  cursor = '',
  htcAuthKey = '',
  isSSR = false,
}: {
  roomId: string
  parentId?: string
  limit?: number
  cursor?: string
  htcAuthKey?: string
  isSSR?: boolean
}) => {
  const queryParams = new URLSearchParams({
    room_id: roomId,
    ...(parentId && { parent_id: parentId }),
    limit: limit.toString(),
    cursor: cursor,
  })
  const response = await fetchCMSApiWithStatusCode<CommentsData>(
    `${API_ENDPOINT_COMMENTS_V2}?${queryParams.toString()}`,
    'GET',
    null,
    {},
    htcAuthKey,
    isSSR,
  )

  if (response?.status !== 200) return EMPTY_COMMENTS_DATA

  return response.results as CommentsData
}

export const getCommentsCount = async ({
  roomId,
  isSSR = false,
}: {
  roomId: string
  isSSR?: boolean
}): Promise<CommentsData> => {
  const queryParams = new URLSearchParams({
    room_id: roomId,
  })

  const response = await fetchCMSApiWithStatusCode<CommentsData>(
    `${API_ENDPOINT_COMMENTS_COUNT}?${queryParams.toString()}`,
    'GET',
    null,
    {},
    '',
    isSSR,
  )

  if (response?.status !== 200) return EMPTY_COMMENTS_DATA

  return response.results as CommentsData
}

export async function checkUserVoucher(worldId: string, profileId: string) {
  const result = await fetchCMSApiWithStatusCode<{ status: number }>(
    `${API_GET_HUBS}/${worldId}/voucher-check?account_id=${profileId}`,
    'GET',
    null,
    {},
  )
  return result?.status === 200
}

export function getVoucherIds(
  htcToken: string,
  profileId: string,
  chain: string = 'digital_asset',
  vendorType: VendorType = 'vivebytes',
) {
  return fetchCMSApiWithStatusCode<EntryPassResults>(
    `${API_GET_NFT}/${profileId}/nft/${chain}/${vendorType}/vouchers`,
    'GET',
    null,
    {},
    htcToken,
  )
}

export function getVoucherRooms(voucherId: string) {
  const queryParams = new URLSearchParams({
    voucher_id: voucherId,
  })
  return fetchCMSApiWithStatusCode<PaginatedResponse<Room>>(
    `${API_GET_VOUCHER_ROOMS_V2}?${queryParams.toString()}`,
    'GET',
    null,
    {},
  )
}
